import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { GraduationCap, Loader2 } from "lucide-react";
import AudioListen from "@/components/AudioListen";
import { GraduationShare } from "@/components/GraduationShare";
import { WallPost } from "@/components/WallPost";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const congratsText = (name) =>
  `${name}, you made it through. Eight modules. Eight rooms you walked into and did not leave until the work was done. ` +
  `Through adversity comes change — and you are the proof. This is not the end of the practice. It is the day the practice became yours. ` +
  `Walk into your next shift with your feet under you. We are proud of you.`;

export const GraduationBlock = ({ hasVideo, firstName, fullName = "", onWall = false, wallDisplay = null, photo = null, wallShowPhoto = false }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(hasVideo);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    if (!hasVideo) return;
    let alive = true;
    axios.get(`${API}/modules/graduation/video`, { responseType: "blob" })
      .then((res) => {
        if (!alive) return;
        const url = URL.createObjectURL(res.data);
        objectUrlRef.current = url;
        setVideoUrl(url);
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => {
      alive = false;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [hasVideo]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
      className="tacc-card p-8 md:p-12 mt-6 border-[#F59E0B]/40"
      data-testid="graduation-block"
    >
      <div className="flex items-center gap-3 mb-4">
        <GraduationCap size={26} className="text-[#B45309]" strokeWidth={1.3} />
        <div className="section-tag">§ Graduation · All 8 complete</div>
      </div>
      <h3 className="font-display text-4xl md:text-5xl tracking-tight mb-4">You made it through, {firstName}.</h3>
      {hasVideo ? (
        <div className="aspect-video bg-black flex items-center justify-center overflow-hidden mt-6">
          {loading && <Loader2 size={24} className="animate-spin text-[#B45309]" />}
          {videoUrl && (
            <video
              data-testid="graduation-video"
              src={videoUrl}
              controls
              controlsList="nodownload"
              disablePictureInPicture
              className="w-full h-full"
              onContextMenu={(e) => e.preventDefault()}
            />
          )}
        </div>
      ) : (
        <div className="max-w-xl">
          <p className="text-[#78716C] text-sm leading-relaxed mb-5">A personal message from Rich, narrated in his voice.</p>
          <AudioListen text={congratsText(firstName)} label="Play your graduation message" testid="graduation-audio" />
        </div>
      )}
      <GraduationShare firstName={firstName} />
      {onWall && <WallPost fullName={fullName} initialDisplay={wallDisplay} photo={photo} initialShowPhoto={wallShowPhoto} />}
    </motion.div>
  );
};
